import { useMemo, useState, useRef, useEffect, useCallback, memo } from 'react'
import Image from 'next/image'
import { motion, AnimatePresence } from 'framer-motion'

import { Annotation, Annotation2 } from '@/types/annotation'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import {
  ExternalLink,
  X,
  ChevronLeft,
  ChevronRight,
  Copy,
  Share2,
  Expand,
  Grid,
  List,
  Filter,
  Save
} from 'lucide-react'
import { toast } from 'sonner'
import { getGoogleFaviconUrl } from '@/lib/favicon'
import { YouTubeVisualizer } from '@/components/messages/youtube-visualizer'
import {
  parseYoutubeVideoMarkerPayload,
  type YoutubeVideoMarker
} from '@/lib/youtube-markers'

// --------------
// Types

type WebSearchResult = {
  organic?: OrganicResult[]
  images?: ImageItem[]
  videos?: OrganicResult[]
  relatedSearches?: string[]
}

type WebSearchAnnotationValue = WebSearchResult | { result?: WebSearchResult } | null | undefined

// Constants
const SCROLL_AMOUNT = 300
const SCROLL_CHECK_DELAY = 100
const MAX_IMAGES_DISPLAY = 20
const LINKS_CARD_WIDTH = 256 // w-64
const IMAGES_CARD_WIDTH = 144 // w-36

// Import the OrganicResult type
type OrganicResult = {
  title: string
  link: string
  snippet: string
  sitelinks?: { title: string; link: string }[]
  position?: number
  attributes?: Record<string, string>
  date?: string
  thumbnail?: string
  image?: { title?: string; url?: string; imageUrl?: string } | string | null
}

type ImageItem = {
  title: string
  imageUrl: string
  url: string
}

function coerceOrganicImageUrl(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed || trimmed === 'null') return null
    if (!/^https?:\/\//i.test(trimmed)) return null
    return trimmed
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>
    return (
      coerceOrganicImageUrl(record.imageUrl) ||
      coerceOrganicImageUrl(record.url) ||
      coerceOrganicImageUrl(record.src) ||
      coerceOrganicImageUrl(record.thumbnail)
    )
  }
  return null
}

function imagesFromOrganicResults(organic: OrganicResult[] | undefined): ImageItem[] {
  if (!organic?.length) return []

  const images: ImageItem[] = []
  const seen = new Set<string>()

  for (const result of organic) {
    const imageUrl =
      coerceOrganicImageUrl(result.thumbnail) || coerceOrganicImageUrl(result.image)
    if (!imageUrl || seen.has(imageUrl)) continue
    seen.add(imageUrl)
    images.push({
      title: result.title || 'Image',
      imageUrl,
      url: result.link || imageUrl
    })
  }

  return images
}

function dedupeImages(images: ImageItem[]): ImageItem[] {
  const seen = new Set<string>()
  const out: ImageItem[] = []
  for (const image of images) {
    if (!image?.imageUrl || seen.has(image.imageUrl)) continue
    seen.add(image.imageUrl)
    out.push(image)
  }
  return out
}

type ScrollPosition = {
  canScrollLeft: boolean
  canScrollRight: boolean
}

type ViewMode = 'scroller' | 'grid' | 'list'

const getSearchResultAttribute = (
  attributes: Record<string, string> | undefined,
  keys: string[]
) => {
  if (!attributes) return ''

  const entries = Object.entries(attributes)
  const match = entries.find(([key]) =>
    keys.some((candidate) => key.toLowerCase() === candidate.toLowerCase())
  )

  return match?.[1] ?? ''
}

const getYoutubeVideoFromResult = (
  item: OrganicResult
): YoutubeVideoMarker | null =>
  parseYoutubeVideoMarkerPayload(
    JSON.stringify({
      url: item.link,
      title: item.title,
      channel: getSearchResultAttribute(item.attributes, [
        'channel',
        'author',
        'source'
      ]),
      duration: getSearchResultAttribute(item.attributes, ['duration']),
      summary: item.snippet,
      thumbnailUrl: item.thumbnail
    })
  )

const WebSearchComponent = ({ annotation }: { annotation: Annotation | Annotation2 }) => {
  // All hooks must be at the top before any conditional logic
  const [selectedImage, setSelectedImage] = useState<ImageItem | null>(null)
  const [linksScrollPosition, setLinksScrollPosition] = useState<ScrollPosition>({
    canScrollLeft: false,
    canScrollRight: false
  })
  const [imagesScrollPosition, setImagesScrollPosition] = useState<ScrollPosition>({
    canScrollLeft: false,
    canScrollRight: false
  })
  const [linksViewMode, setLinksViewMode] = useState<ViewMode>('scroller')
  const [showAllImages, setShowAllImages] = useState(false)
  const [savedItems, setSavedItems] = useState<Set<string>>(new Set())
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<number>>(new Set())
  const [failedImages, setFailedImages] = useState<Set<number>>(new Set())
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(-1)
  const [direction, setDirection] = useState<number>(0)
  const touchStartXRef = useRef<number | null>(null)

  const linksScrollRef = useRef<HTMLDivElement>(null)
  const imagesScrollRef = useRef<HTMLDivElement>(null)

  // Extract data early for hooks
  const raw = (annotation as any).webScraper__googleSearch as WebSearchAnnotationValue | undefined

  const results = Array.isArray(raw) ? raw : raw ? [raw] : []

  const normalizedResults: WebSearchResult[] = results
    .map((r) => {
      let value: unknown = r
      if (typeof value === 'string') {
        const trimmed = value.trim()
        if (
          (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
          (trimmed.startsWith('[') && trimmed.endsWith(']'))
        ) {
          try {
            value = JSON.parse(trimmed)
          } catch {
            return null
          }
        } else {
          return null
        }
      }

      if (value && typeof value === 'object' && 'result' in (value as any)) {
        value = (value as any).result
      }

      return value as WebSearchResult
    })
    .filter((r): r is WebSearchResult => Boolean(r) && typeof r === 'object')

  const combined = useMemo((): WebSearchResult => {
    const organic = normalizedResults.flatMap((r) => r?.organic ?? [])
    const images = normalizedResults.flatMap((r) => r?.images ?? [])
    const videos = normalizedResults.flatMap((r) => (r as any)?.videos ?? [])
    const related = normalizedResults.flatMap((r) => (r as any)?.relatedSearches ?? [])

    // Merge into a single shape that the existing UI can render without
    // overwriting earlier searches.
    return {
      ...(normalizedResults[0] ?? ({} as WebSearchResult)),
      organic,
      images,
      ...(videos.length ? { videos } : {}),
      ...(related.length ? { relatedSearches: related } : {})
    } as WebSearchResult
  }, [normalizedResults])

  const organic = combined?.organic

  // Memoize images to prevent flashing during streaming updates.
  // Prefer dedicated gallery images; fall back to organic thumbnails when empty.
  const images = useMemo(() => {
    const fromApi = dedupeImages(combined?.images || [])
    if (fromApi.length > 0) return fromApi
    return imagesFromOrganicResults(combined?.organic)
  }, [combined?.images, combined?.organic])

  // Handle image errors - moved here to be available for memoized elements
  const handleImageError = useCallback((index: number) => {
    setFailedImages((prev) => new Set(prev).add(index))
  }, [])

  // Memoize rendered images to prevent unnecessary re-renders
  const memoizedImageElements = useMemo(() => {
    if (!images || images.length === 0) return null

    return (showAllImages ? images : images.slice(0, MAX_IMAGES_DISPLAY))
      .filter((_: ImageItem, index: number) => !failedImages.has(index))
      .map((image: ImageItem, displayIndex: number) => {
        const originalIndex = images.indexOf(image)
        return (
          <div
            key={`image-${originalIndex}-${image.imageUrl}`}
            className="pointer-events-auto shrink-0 cursor-pointer"
            onClick={() => {
              setSelectedImage(image)
              setSelectedImageIndex(originalIndex)
            }}
            onPointerUp={(e: React.PointerEvent<HTMLDivElement>) => {
              e.preventDefault()
              setSelectedImage(image)
              setSelectedImageIndex(originalIndex)
            }}
            onTouchEnd={(e) => {
              e.preventDefault()
              setSelectedImage(image)
              setSelectedImageIndex(originalIndex)
            }}
            role="button"
            tabIndex={0}
            aria-label={`View image: ${image.title || 'Untitled'}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setSelectedImage(image)
                setSelectedImageIndex(originalIndex)
              }
            }}
          >
            <div className="group relative h-36 w-48 overflow-hidden rounded-lg bg-muted/50 hover:shadow-lg motion-safe:transition-[box-shadow] motion-safe:duration-200">
              <img
                src={image.imageUrl}
                alt={image.title || 'Search result image'}
                className="size-full object-cover transition-transform duration-200 hover:scale-105"
                loading="lazy"
                onError={() => handleImageError(originalIndex)}
              />
              {/* Expand icon only on image hover */}
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/20 motion-safe:transition-colors motion-safe:duration-200">
                <div className="opacity-0 hover:opacity-100 motion-safe:transition-opacity motion-safe:duration-200">
                  <Expand className="size-6 text-white drop-shadow-lg" />
                </div>
              </div>
            </div>
          </div>
        )
      })
  }, [images, showAllImages, failedImages, setSelectedImage, handleImageError])

  const youtubeVideos = useMemo(() => {
    const candidates = [...(organic ?? []), ...(combined?.videos ?? [])]
    const uniqueVideos = new Map<string, YoutubeVideoMarker>()

    candidates.forEach((item) => {
      const video = getYoutubeVideoFromResult(item)
      if (video) uniqueVideos.set(video.id, video)
    })

    return Array.from(uniqueVideos.values())
  }, [combined?.videos, organic])

  const links = useMemo(
    () =>
      organic?.filter((item: OrganicResult) => {
        if (item.link && typeof item.link === 'string') {
          try {
            return !item.link.includes('youtube.com/watch?v=')
          } catch (e) {
            return false
          }
        }
        return false
      }) || [],
    [organic]
  )

  // Get user preferences from localStorage
  useEffect(() => {
    const savedViewMode = localStorage.getItem('websearch-links-view-mode') as ViewMode
    if (savedViewMode && ['scroller', 'grid', 'list'].includes(savedViewMode)) {
      setLinksViewMode(savedViewMode)
    }

    const savedItemsStr = localStorage.getItem('websearch-saved-items')
    if (savedItemsStr) {
      try {
        setSavedItems(new Set(JSON.parse(savedItemsStr)))
      } catch (e) {
        console.error('Failed to parse saved items:', e)
      }
    }
  }, [])

  // Save preferences to localStorage
  const updateViewMode = useCallback((mode: ViewMode) => {
    setLinksViewMode(mode)
    localStorage.setItem('websearch-links-view-mode', mode)
  }, [])

  const toggleSavedItem = useCallback((url: string) => {
    setSavedItems((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(url)) {
        newSet.delete(url)
        toast.success('Removed from saved items')
      } else {
        newSet.add(url)
        toast.success('Saved for later')
      }
      localStorage.setItem('websearch-saved-items', JSON.stringify([...newSet]))
      return newSet
    })
  }, [])

  const toggleDescription = useCallback((index: number) => {
    setExpandedDescriptions((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(index)) {
        newSet.delete(index)
      } else {
        newSet.add(index)
      }
      return newSet
    })
  }, [])

  // Utility functions with improved error handling
  const copyToClipboard = useCallback(async (text: string, type: string) => {
    try {
      if (!navigator.clipboard) {
        throw new Error('Clipboard API not available')
      }
      await navigator.clipboard.writeText(text)
      toast.success(`${type} copied to clipboard`)
    } catch (err) {
      // Fallback for older browsers
      try {
        const textArea = document.createElement('textarea')
        textArea.value = text
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        toast.success(`${type} copied to clipboard`)
      } catch (fallbackErr) {
        console.error('Copy failed:', err, fallbackErr)
        toast.error(`Failed to copy ${type.toLowerCase()}. Please copy manually.`)
      }
    }
  }, [])

  const shareLink = useCallback(
    async (url: string, title: string) => {
      if (navigator.share) {
        try {
          await navigator.share({ title, url })
          toast.success('Link shared successfully')
        } catch (err) {
          if ((err as Error).name !== 'AbortError') {
            copyToClipboard(url, 'Link')
          }
        }
      } else {
        copyToClipboard(url, 'Link')
      }
    },
    [copyToClipboard]
  )

  // Improved scroll utilities with better performance
  const checkScrollPosition = useCallback(
    (element: HTMLDivElement | null, setState: (state: ScrollPosition) => void) => {
      if (!element) return

      const canScrollLeft = element.scrollLeft > 1 // Small threshold for precision
      const canScrollRight = element.scrollLeft < element.scrollWidth - element.clientWidth - 1

      setState({ canScrollLeft, canScrollRight })
    },
    []
  )

  const scrollContainer = useCallback(
    (ref: React.RefObject<HTMLDivElement>, direction: 'left' | 'right') => {
      if (!ref.current) return

      const scrollAmount = direction === 'left' ? -SCROLL_AMOUNT : SCROLL_AMOUNT
      ref.current.scrollBy({ left: scrollAmount, behavior: 'smooth' })
    },
    []
  )

  // Monitor scroll positions with improved performance
  useEffect(() => {
    const linksElement = linksScrollRef.current
    const imagesElement = imagesScrollRef.current

    const handleLinksScroll = () => checkScrollPosition(linksElement, setLinksScrollPosition)
    const handleImagesScroll = () => checkScrollPosition(imagesElement, setImagesScrollPosition)

    // Use requestAnimationFrame for better performance
    const scheduleCheck = (callback: () => void) => {
      setTimeout(() => requestAnimationFrame(callback), SCROLL_CHECK_DELAY)
    }

    if (linksElement && linksViewMode === 'scroller') {
      linksElement.addEventListener('scroll', handleLinksScroll, { passive: true })
      scheduleCheck(handleLinksScroll)
    }

    if (imagesElement) {
      imagesElement.addEventListener('scroll', handleImagesScroll, { passive: true })
      scheduleCheck(handleImagesScroll)
    }

    return () => {
      linksElement?.removeEventListener('scroll', handleLinksScroll)
      imagesElement?.removeEventListener('scroll', handleImagesScroll)
    }
  }, [checkScrollPosition, linksViewMode])

  // Render Link Card Component
  const renderLinkCard = useCallback(
    (item: OrganicResult, index: number, className?: string) => {
      if (!item.link || typeof item.link !== 'string') {
        return null
      }

      try {
        const hostname = new URL(item.link).hostname
        const displayHostname = hostname.replace('www.', '')
        const thumbnail = (item as any).thumbnail
        const isExpanded = expandedDescriptions.has(index)
        const isSaved = savedItems.has(item.link)
        const truncatedSnippet =
          item.snippet && item.snippet.length > 120
            ? item.snippet.substring(0, 120) + '...'
            : item.snippet

        return (
          <Card
            className={`group relative overflow-hidden border border-border/50 hover:border-border hover:bg-accent motion-safe:transition-[background-color,border-color] motion-safe:duration-200 ${className || ''}`}
            key={index}
          >
            {/* Thumbnail background */}
            {thumbnail && thumbnail !== '' && (
              <div
                className="absolute inset-0 bg-cover bg-center opacity-20"
                style={{
                  backgroundImage: `url(${thumbnail})`,
                  filter: 'blur(8px) brightness(0.3)'
                }}
              />
            )}

            {/* Content */}
            <div className="relative z-10 p-3">
              {/* Header with favicon and hostname */}
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="flex size-[16px] items-center justify-center rounded-full bg-background text-[10px] text-background">
                    <Image
                      src={getGoogleFaviconUrl(hostname, 64) ?? '/favicon.ico'}
                      alt={hostname}
                      width={16}
                      height={16}
                    />
                  </div>
                  <div className="text-xs font-semibold text-muted-foreground">
                    {displayHostname}
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex items-center space-x-1 opacity-0 transition-opacity group-hover:opacity-100">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="size-6 p-0"
                    onClick={(e) => {
                      e.preventDefault()
                      toggleSavedItem(item.link)
                    }}
                    aria-label={isSaved ? 'Remove from saved' : 'Save for later'}
                  >
                    <Save className={`size-3 ${isSaved ? 'fill-current text-primary' : ''}`} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="size-6 p-0"
                    onClick={(e) => {
                      e.preventDefault()
                      copyToClipboard(item.link, 'Link')
                    }}
                    aria-label="Copy link"
                  >
                    <Copy className="size-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="size-6 p-0"
                    onClick={(e) => {
                      e.preventDefault()
                      shareLink(item.link, item.title)
                    }}
                    aria-label="Share link"
                  >
                    <Share2 className="size-3" />
                  </Button>
                </div>
              </div>

              {/* Title */}
              <a
                href={item.link}
                target="_blank"
                className="block transition-colors hover:text-primary"
              >
                <div
                  className={`font-medium ${linksViewMode === 'list' ? 'text-sm' : 'text-xs'} mb-2 line-clamp-2`}
                >
                  {item.title}
                </div>
              </a>

              {/* Snippet with show more/less functionality */}
              {item.snippet && (
                <div className="text-xs text-muted-foreground">
                  <p className={isExpanded ? '' : 'line-clamp-2'}>
                    {isExpanded ? item.snippet : truncatedSnippet}
                  </p>
                  {item.snippet.length > 120 && (
                    <button
                      onClick={() => toggleDescription(index)}
                      className="mt-1 text-xs font-medium text-primary hover:underline"
                    >
                      {isExpanded ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </div>
              )}

              {/* Date if available */}
              {item.date && (
                <div className="mt-2 text-xs text-muted-foreground">
                  <Badge variant="secondary" className="text-xs">
                    {item.date}
                  </Badge>
                </div>
              )}
            </div>
          </Card>
        )
      } catch (e) {
        return null
      }
    },
    [
      expandedDescriptions,
      savedItems,
      toggleSavedItem,
      copyToClipboard,
      shareLink,
      toggleDescription,
      linksViewMode
    ]
  )

  // Navigation function using selectedImageIndex state
  const navigateImage = useCallback(
    (dir: 'prev' | 'next') => {
      if (selectedImageIndex < 0 || images.length === 0) return

      const newIndex =
        dir === 'prev'
          ? (selectedImageIndex - 1 + images.length) % images.length
          : (selectedImageIndex + 1) % images.length
      setDirection(dir === 'prev' ? -1 : 1)
      setSelectedImage(images[newIndex])
      setSelectedImageIndex(newIndex)
    },
    [selectedImageIndex, images]
  )

  // Keyboard navigation using arrow keys
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (selectedImageIndex < 0) return
      if (e.key === 'ArrowLeft') {
        navigateImage('prev')
      } else if (e.key === 'ArrowRight') {
        navigateImage('next')
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedImageIndex, navigateImage])

  // Now handle the data after all hooks are defined
  // Early returns after all hooks
  if (normalizedResults.length === 0) {
    return null
  }

  // Early return if no links and no images (images-only searches should still render)
  if ((!organic || organic.length === 0) && (!images || images.length === 0)) {
    return null
  }

  // --------------

  return (
    <div className="space-y-6">
      {/* Web Results Section */}
      {links && links.length > 0 && (
        <div className="space-y-3">
          {/* Section Header with Controls */}
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">Web Results ({links.length})</h3>

            {/* View Mode Toggle */}
            <div className="flex items-center space-x-2">
              <div className="flex items-center rounded-md bg-muted p-1">
                <Button
                  variant={linksViewMode === 'scroller' ? 'default' : 'ghost'}
                  size="sm"
                  className="h-6 px-2"
                  onClick={() => updateViewMode('scroller')}
                  aria-label="Horizontal scroller view"
                >
                  <ChevronRight className="size-3" />
                </Button>
                <div className="hidden md:block">
                  <Button
                    variant={linksViewMode === 'grid' ? 'default' : 'ghost'}
                    size="sm"
                    className="h-6 px-2"
                    onClick={() => updateViewMode('grid')}
                    aria-label="Grid view"
                  >
                    <Grid className="size-3" />
                  </Button>
                </div>
                <Button
                  variant={linksViewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  className="h-6 px-2"
                  onClick={() => updateViewMode('list')}
                  aria-label="List view"
                >
                  <List className="size-3" />
                </Button>
              </div>
            </div>
          </div>

          {/* Content based on view mode */}
          {linksViewMode === 'scroller' && (
            <div className="relative">
              {/* Scroll Controls */}
              <Button
                variant="outline"
                size="sm"
                className={`absolute left-2 top-1/2 size-8 -translate-y-1/2 bg-background/80 p-0 backdrop-blur-sm ${
                  !linksScrollPosition.canScrollLeft
                    ? 'cursor-not-allowed opacity-50'
                    : 'hover:bg-muted'
                }`}
                onClick={() => scrollContainer(linksScrollRef, 'left')}
                disabled={!linksScrollPosition.canScrollLeft}
                aria-label="Scroll left"
                style={{ zIndex: 15 }}
              >
                <ChevronLeft className="size-4" />
              </Button>

              <Button
                variant="outline"
                size="sm"
                className={`absolute right-2 top-1/2 size-8 -translate-y-1/2 bg-background/80 p-0 backdrop-blur-sm ${
                  !linksScrollPosition.canScrollRight
                    ? 'cursor-not-allowed opacity-50'
                    : 'hover:bg-muted'
                }`}
                onClick={() => scrollContainer(linksScrollRef, 'right')}
                disabled={!linksScrollPosition.canScrollRight}
                aria-label="Scroll right"
                style={{ zIndex: 15 }}
              >
                <ChevronRight className="size-4" />
              </Button>

              {/* Fade overlays */}
              {linksScrollPosition.canScrollLeft && (
                <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-background to-transparent" />
              )}
              {linksScrollPosition.canScrollRight && (
                <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-background to-transparent" />
              )}

              <div
                ref={linksScrollRef}
                className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 flex space-x-3 overflow-x-auto scroll-smooth pb-2"
              >
                {links.map((item: OrganicResult, index: number) =>
                  renderLinkCard(item, index, 'flex-shrink-0 w-64')
                )}
              </div>
            </div>
          )}

          {linksViewMode === 'grid' && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              {links.map((item: OrganicResult, index: number) => renderLinkCard(item, index))}
            </div>
          )}

          {linksViewMode === 'list' && (
            <div className="space-y-2">
              {links.map((item: OrganicResult, index: number) =>
                renderLinkCard(item, index, 'w-full')
              )}
            </div>
          )}
        </div>
      )}

      {/* Images Section */}
      {images && images.length > 0 && (
        <div className="space-y-3">
          {/* Section Header */}
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">
              Images (
              {images.filter((_: ImageItem, index: number) => !failedImages.has(index)).length})
            </h3>

            {images.filter((_, index) => !failedImages.has(index)).length > MAX_IMAGES_DISPLAY && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAllImages(!showAllImages)}
                className="text-xs"
              >
                {showAllImages
                  ? 'Show Less'
                  : `Show All (${images.filter((_, index) => !failedImages.has(index)).length})`}
              </Button>
            )}
          </div>

          <div className="relative">
            {/* Scroll Controls for Images */}
            <Button
              variant="outline"
              size="sm"
              className={`absolute left-2 top-1/2 size-8 -translate-y-1/2 bg-background/80 p-0 backdrop-blur-sm ${
                !imagesScrollPosition.canScrollLeft
                  ? 'cursor-not-allowed opacity-50'
                  : 'hover:bg-muted'
              }`}
              onClick={() => scrollContainer(imagesScrollRef, 'left')}
              disabled={!imagesScrollPosition.canScrollLeft}
              aria-label="Scroll images left"
              style={{ zIndex: 15 }}
            >
              <ChevronLeft className="size-4" />
            </Button>

            <Button
              variant="outline"
              size="sm"
              className={`absolute right-2 top-1/2 size-8 -translate-y-1/2 bg-background/80 p-0 backdrop-blur-sm ${
                !imagesScrollPosition.canScrollRight
                  ? 'cursor-not-allowed opacity-50'
                  : 'hover:bg-muted'
              }`}
              onClick={() => scrollContainer(imagesScrollRef, 'right')}
              disabled={!imagesScrollPosition.canScrollRight}
              aria-label="Scroll images right"
              style={{ zIndex: 15 }}
            >
              <ChevronRight className="size-4" />
            </Button>

            {/* Fade overlays */}
            {imagesScrollPosition.canScrollLeft && (
              <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-background to-transparent" />
            )}
            {imagesScrollPosition.canScrollRight && (
              <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-background to-transparent" />
            )}

            <div
              ref={imagesScrollRef}
              className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 flex space-x-3 overflow-x-auto scroll-smooth pb-2"
            >
              {memoizedImageElements}
            </div>
          </div>
        </div>
      )}

      {/* Videos Section */}
      {youtubeVideos.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-foreground">
            Videos ({youtubeVideos.length})
          </h3>
          <YouTubeVisualizer title="YouTube results" videos={youtubeVideos} />
        </div>
      )}

      {/* Enhanced Image Preview Dialog with Dark Mode Support */}
      <Dialog
        open={!!selectedImage}
        onOpenChange={(isOpen) => {
          if (!isOpen) setSelectedImage(null)
        }}
      >
        <DialogContent className="max-h-[95vh] max-w-6xl border-border bg-background p-0">
          <DialogHeader className="border-b border-border p-4 pb-2">
            <DialogTitle className="sr-only">Image Preview</DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-4 top-4 size-8 p-0 hover:bg-muted"
              onClick={() => setSelectedImage(null)}
              aria-label="Close image preview"
            >
              <X className="size-4" />
            </Button>
          </DialogHeader>

          {selectedImage && (
            <div className="flex h-full flex-col">
              {/* Image Container - Maximum visibility */}
              <motion.div
                className="relative flex max-h-[80vh] min-h-[70vh] flex-1 items-center justify-center overflow-hidden bg-muted/20"
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                onDragEnd={(_, info) => {
                  if (info.offset.x > 50) navigateImage('prev')
                  else if (info.offset.x < -50) navigateImage('next')
                }}
              >
                {/* Navigation Buttons */}
                <Button
                  variant="outline"
                  size="sm"
                  className="absolute left-4 top-1/2 z-20 size-10 -translate-y-1/2 bg-background/80 p-0 backdrop-blur-sm hover:bg-muted"
                  onClick={() => navigateImage('prev')}
                  aria-label="Previous image"
                >
                  <ChevronLeft className="size-6" />
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  className="absolute right-4 top-1/2 z-20 size-10 -translate-y-1/2 bg-background/80 p-0 backdrop-blur-sm hover:bg-muted"
                  onClick={() => navigateImage('next')}
                  aria-label="Next image"
                >
                  <ChevronRight className="size-6" />
                </Button>

                <AnimatePresence initial={false} custom={direction} mode="wait">
                  <motion.img
                    key={selectedImage!.imageUrl}
                    src={selectedImage!.imageUrl}
                    alt={selectedImage!.title || 'Expanded image'}
                    className="max-h-full max-w-full object-contain"
                    initial={{ x: direction * 300, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: -direction * 300, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  />
                </AnimatePresence>
              </motion.div>

              {/* Footer with dark mode support */}
              <div className="space-y-3 border-t border-border bg-background p-4">
                {selectedImage!.title && (
                  <div>
                    <h3 className="text-sm font-medium leading-relaxed text-foreground">
                      {selectedImage!.title}
                    </h3>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  {/* Navigation instructions can go here if needed */}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

// wrap in memo to avoid remounts on parent updates
export const WebSearch = memo(WebSearchComponent)
